import React, { useContext, useEffect, useState } from 'react';
import { CompanyRelationService, IntegrationSettingsInput } from '../../../../../../../types/integration_settings';
import { IntegrationModalState } from '../../IntegrationModal';
import TeamleaderIntegrationConnectPane from './TeamleaderIntegrationConnectPane';
import TeamleaderIntegrationInfoPane from './TeamleaderIntegrationInfoPane';
import useMutation from '../../../../../../../hooks/useMutation';
import {
  DeleteIntegrationSettingsResponse,
  DeleteIntegrationSettingsVariables,
  IntegrationSettingsMutations,
} from '../../../../../../../graphql/integrationSettings.graphql';
import { IntegrationContext } from '../../../../../../../context/IntegrationContext';
import { useToast } from '../../../../../../../providers/ToastProvider';
import { ToastType } from '../../../../../../components/Toast';
import { TranslationKey } from '../../../../../../../types/translation/TranslationKey';
import { t } from '../../../../../../../types/translation/Translator';

interface TeamleaderIntegrationPaneProps {
  input: IntegrationSettingsInput;
  setInput: (input: IntegrationSettingsInput) => void;

  handleCancel: () => void;
}

export enum TeamleaderIntegrationPaneState {
  connect = 'connect',
  info = 'info',
}

export default function TeamleaderIntegrationPane({ input, setInput, handleCancel }: TeamleaderIntegrationPaneProps) {
  if (input.type !== CompanyRelationService.teamleader) return null;

  const { showToast } = useToast();
  const { integrationSettings, setIntegrationSettings } = useContext(IntegrationContext);

  const determineType = () => {
    if (!input.id) return TeamleaderIntegrationPaneState.connect;
    return IntegrationModalState.info;
  };

  const [state, setState] = useState(determineType());

  const [remove, { loading }] = useMutation<DeleteIntegrationSettingsResponse, DeleteIntegrationSettingsVariables>(
    IntegrationSettingsMutations.remove,
    {
      onCompleted: response => {
        integrationSettings.delete(response.deleteIntegrationSettings[0].id);
        setIntegrationSettings(new Map(integrationSettings));
        showToast({
          type: ToastType.success,
          title: t(TranslationKey.integrationDeletedSuccessfullyToastTitle).singular.upper,
        });
        handleCancel();
      },
    },
  );

  useEffect(() => {
    if (input.version) return;

    setState(determineType());
  }, [input]);

  const handleDelete = async () => {
    await remove({ variables: { settings: [input.forDelete()] } });
  };

  if (state === TeamleaderIntegrationPaneState.connect) {
    return <TeamleaderIntegrationConnectPane input={input} setInput={setInput} handleCancel={handleCancel} />;
  } else if (state === TeamleaderIntegrationPaneState.info) {
    return (
      <TeamleaderIntegrationInfoPane
        input={input}
        setInput={setInput}
        handleCancel={handleCancel}
        handleDelete={handleDelete}
        loading={loading}
      />
    );
  }

  return <></>;
}
